// SPDX-FileCopyrightText: Copyright Orangebot, Inc.
// SPDX-License-Identifier: Apache-2.0

/**
 * Encounter Linking Utilities
 *
 * Smart encounter linking for CareComm communications.
 * Handles auto-linking when only one encounter exists, and provides
 * information for manual selection when multiple encounters are active.
 */

import type { MedplumClient } from '@medplum/core';
import type { Encounter } from '@medplum/fhirtypes';

/**
 * Result of smart encounter linking
 */
export interface SmartEncounterLinkResult {
  /** The encounter ID to link to (if auto-selected or explicitly provided) */
  encounterId?: string;
  /** Whether user needs to manually select from multiple encounters */
  needsSelection: boolean;
  /** List of active encounters (for selection UI) */
  encounters: Encounter[];
}

/**
 * Active encounter statuses that indicate an ongoing visit
 */
const ACTIVE_ENCOUNTER_STATUSES = ['in-progress', 'arrived', 'planned'];

/**
 * Find all active encounters for a patient
 *
 * Active encounters are those with status: in-progress, arrived, or planned
 */
export async function findActiveEncountersForPatient(
  medplum: MedplumClient,
  patientId: string,
  organizationId?: string
): Promise<Encounter[]> {
  const searchParams: Record<string, string> = {
    subject: `Patient/${patientId}`,
    status: ACTIVE_ENCOUNTER_STATUSES.join(','),
    _sort: '-date',
    _count: '10',
  };

  // Optionally filter by organization
  if (organizationId) {
    searchParams['service-provider'] = `Organization/${organizationId}`;
  }

  try {
    return await medplum.searchResources('Encounter', searchParams);
  } catch (error) {
    console.error('Error finding active encounters for patient:', error);
    return [];
  }
}

/**
 * Smart encounter linking
 *
 * Determines how to link a communication to an encounter:
 * - If explicit encounterId is provided → use it directly
 * - If 1 active encounter exists → auto-link to it
 * - If multiple active encounters → return list for manual selection
 * - If 0 active encounters → no linking
 *
 * @param medplum - Medplum client instance
 * @param patientId - Patient ID to find encounters for
 * @param explicitEncounterId - Optional explicit encounter ID to use
 * @param organizationId - Optional organization ID to filter encounters
 * @returns Smart linking result with encounterId, needsSelection flag, and encounters list
 */
export async function getSmartEncounterLink(
  medplum: MedplumClient,
  patientId: string,
  explicitEncounterId?: string,
  organizationId?: string
): Promise<SmartEncounterLinkResult> {
  // If explicit encounter ID is provided, use it directly
  if (explicitEncounterId) {
    return {
      encounterId: explicitEncounterId,
      needsSelection: false,
      encounters: [],
    };
  }

  // Find active encounters for the patient
  const encounters = await findActiveEncountersForPatient(medplum, patientId, organizationId);

  if (encounters.length === 0) {
    // No active encounters - no linking
    return {
      encounterId: undefined,
      needsSelection: false,
      encounters: [],
    };
  }

  if (encounters.length === 1) {
    // Exactly one active encounter - auto-link
    return {
      encounterId: encounters[0].id,
      needsSelection: false,
      encounters,
    };
  }

  // Multiple active encounters - need manual selection
  return {
    encounterId: undefined,
    needsSelection: true,
    encounters,
  };
}

/**
 * Get encounter ID for linking (simplified version for direct use)
 *
 * Auto-links when only one encounter exists, returns undefined otherwise.
 * Use getSmartEncounterLink for full control over the selection process.
 *
 * @param medplum - Medplum client instance
 * @param patientId - Patient ID to find encounters for
 * @param explicitEncounterId - Optional explicit encounter ID to use
 * @param organizationId - Optional organization ID to filter encounters
 * @returns Encounter ID to link to, or undefined if no auto-linking
 */
export async function getAutoLinkedEncounterId(
  medplum: MedplumClient,
  patientId: string,
  explicitEncounterId?: string,
  organizationId?: string
): Promise<string | undefined> {
  const result = await getSmartEncounterLink(
    medplum,
    patientId,
    explicitEncounterId,
    organizationId
  );

  // Only return encounter ID if we can auto-link (not when selection is needed)
  return result.needsSelection ? undefined : result.encounterId;
}

/**
 * Check if a patient has any active encounters
 *
 * Useful for quick checks before starting communications
 */
export async function hasActiveEncounter(
  medplum: MedplumClient,
  patientId: string,
  organizationId?: string
): Promise<boolean> {
  const encounters = await findActiveEncountersForPatient(medplum, patientId, organizationId);
  return encounters.length > 0;
}

/**
 * Get the most recent active encounter for a patient
 *
 * Prioritizes by status (in-progress > arrived > planned) then by date
 */
export async function getMostRecentActiveEncounter(
  medplum: MedplumClient,
  patientId: string,
  organizationId?: string
): Promise<Encounter | null> {
  const encounters = await findActiveEncountersForPatient(medplum, patientId, organizationId);

  if (encounters.length === 0) {
    return null;
  }

  // Sort by status priority, then by date
  const statusPriority: Record<string, number> = {
    'in-progress': 0,
    arrived: 1,
    planned: 2,
  };

  encounters.sort((a, b) => {
    const priorityA = statusPriority[a.status] ?? 99;
    const priorityB = statusPriority[b.status] ?? 99;

    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    // Same priority - sort by date (most recent first)
    const dateA = a.period?.start ? new Date(a.period.start).getTime() : 0;
    const dateB = b.period?.start ? new Date(b.period.start).getTime() : 0;
    return dateB - dateA;
  });

  return encounters[0];
}

/**
 * Format encounter for display in selection UI
 */
export function formatEncounterForDisplay(encounter: Encounter): string {
  const parts: string[] = [];

  // Add type if available
  const type = encounter.type?.[0]?.text || encounter.type?.[0]?.coding?.[0]?.display;
  if (type) {
    parts.push(type);
  }

  // Add date if available
  if (encounter.period?.start) {
    const date = new Date(encounter.period.start);
    parts.push(date.toLocaleDateString());
  }

  // Add status
  parts.push(`(${encounter.status})`);

  return parts.join(' - ') || `Encounter ${encounter.id}`;
}
